import { KaboodleToaster } from "components/common";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  useDeleteNoteMutation,
  useUpdateNoteMutation,
} from "services/notes-api";
import type { NoteType } from "types/DataTypes";
import { getTimeFromISOString } from "utils/date-utils";
import { useCurrentUser } from "utils/hooks";

import DeleteNoteModal from "./DeleteNoteModal";

type NoteItemProps = {
  note: NoteType;
  onEdit?: (note: NoteType) => void;
};

const NoteItem = ({ note, onEdit }: NoteItemProps) => {
  const currentUser = useCurrentUser();
  const [deleteNote, { isLoading: isDeleting }] = useDeleteNoteMutation();
  const [updateNote, { isLoading: isUpdating }] = useUpdateNoteMutation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(note.text);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const MAX_NOTE_LENGTH = 2000;

  const handleEdit = useCallback(() => {
    setIsEditing(true);
    setEditText(note.text);
  }, [note.text]);

  const handleSave = useCallback(async () => {
    if (editText.trim() === "") {
      KaboodleToaster.error("Note content cannot be empty");
      return;
    }

    if (editText.length > MAX_NOTE_LENGTH) {
      KaboodleToaster.error(`Note cannot exceed ${MAX_NOTE_LENGTH} characters`);
      return;
    }

    if (editText === note.text) {
      setIsEditing(false);
      return;
    }

    try {
      await updateNote({
        id: note.id,
        data: {
          text: editText,
          file: note.file,
          service: note.service || undefined,
          // preserve legacy lien association if it exists
          lien: note.lien || undefined,
          // preserve lien_file association if it exists
          lien_file: note.lien_file || undefined,
          claim: note.claim || undefined,
          mentionee: note.mentionee || null,
        },
      }).unwrap();

      KaboodleToaster.success("Note updated successfully");
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating note:", error);
      KaboodleToaster.error("Failed to update note. Please try again.");
    }
  }, [
    updateNote,
    note.id,
    note.file,
    note.service,
    note.lien,
    note.lien_file,
    note.claim,
    note.mentionee,
    editText,
    note.text,
  ]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditText(note.text);
  }, [note.text]);

  const handleDeleteClick = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      await deleteNote({
        id: note.id,
      }).unwrap();

      KaboodleToaster.success("Note deleted successfully");
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting note:", error);
      KaboodleToaster.error("Failed to delete note. Please try again.");
    }
  }, [deleteNote, note.id]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  // focus textarea when entering edit mode
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      // set cursor to end of text
      const length = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(length, length);
    }
  }, [isEditing]);

  const displayText = isEditing ? editText : note.text;

  const formattedDate = getTimeFromISOString(note.created, "dateTime");

  // check if current user owns this note
  const isOwnNote = currentUser?.id === note.created_by;

  return (
    <div className="Notes__item">
      <div className="Notes__item-content">
        <div className="Notes__item-header">
          <label className="Notes__item-author">
            {note.created_by_name || "Unknown User"}
          </label>
          <label className="Notes__item-date">{formattedDate}</label>
        </div>
        <div className="Notes__item-text">
          {isEditing ? (
            <div className="Notes__item-edit-container">
              <textarea
                ref={textareaRef}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="Notes__item-textarea"
                disabled={isUpdating}
                placeholder="Enter note text..."
                maxLength={MAX_NOTE_LENGTH}
              />
              <div className="Notes__item-edit-actions">
                <button
                  type="button"
                  className="button button--primary"
                  onClick={handleSave}
                  disabled={isUpdating || editText.trim() === ""}
                >
                  {isUpdating ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  className="button button--secondary"
                  onClick={handleCancel}
                  disabled={isUpdating}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            displayText
          )}
        </div>
      </div>
      {onEdit && !isEditing && isOwnNote && (
        <div className="Notes__item-actions">
          <button
            type="button"
            className="material-icons Notes__item-edit"
            onClick={handleEdit}
            title="Edit note"
            disabled={isUpdating}
          >
            edit
          </button>
          <button
            type="button"
            className="material-icons Notes__item-delete"
            onClick={handleDeleteClick}
            disabled={isDeleting || isUpdating}
            title="Delete note"
          >
            delete
          </button>
        </div>
      )}
      <DeleteNoteModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        note={note}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default memo(NoteItem);
